import React, { useState, useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import BatchInvoiceDataRow from './BatchInvoiceDataRow';
import BatchInvoiceTableHeaderRow from './BatchInvoiceTableHeaderRow';

import { saleSelectors } from 'store/sale/selectors';
import { colors, fontSize } from 'styles/cp';
import { UnpaidInvoice } from 'types/Insight';

interface BatchInvoiceMixedPaymentListTableProps {
  batchInvoices: UnpaidInvoice[];
  setBatchInvoices: React.Dispatch<React.SetStateAction<UnpaidInvoice[]>>;
  tooltipStates: Record<string, boolean>;
  currency: string;
  handleCheckboxChange: (invoice: UnpaidInvoice) => void;
  handleShowToolTip: (docNumber: string, show: boolean) => void;
  handleDocumentClick: (invoice: UnpaidInvoice) => void;
  handleNewWindowClick: (link: string) => void;
  maxSelectableBalance: number;
  isDefaultInvoiceOverLimit: boolean;
  partialPaymentAmount?: number | null;
}

const BatchInvoiceMixedPaymentListTable: React.FC<BatchInvoiceMixedPaymentListTableProps> = ({
  batchInvoices,
  setBatchInvoices,
  tooltipStates,
  currency,
  handleCheckboxChange,
  handleShowToolTip,
  handleDocumentClick,
  handleNewWindowClick,
  maxSelectableBalance,
  isDefaultInvoiceOverLimit,
  partialPaymentAmount = null,
}) => {
  const [showAllInvoices, setShowAllInvoices] = useState({
    bankOrCard: false,
    bankOnly: false,
    cardOnly: false,
  });

  const { bankOrCardInvoices, bankOnlyInvoices, cardOnlyInvoices } = useMemo(() => {
    return saleSelectors.categorizeInvoicesByPaymentMethod(batchInvoices);
  }, [batchInvoices]);

  const categories = useMemo(() => {
    const categoriesArray = [
      {
        key: 'bankOrCard' as const,
        label: { id: 'PAYABLE_BY_BANK_CC', defaultMessage: 'Payable by bank or card' },
        invoices: bankOrCardInvoices,
      },
      {
        key: 'bankOnly' as const,
        label: { id: 'PAYABLE_BY_BANK_ONLY', defaultMessage: 'Payable by bank only' },
        invoices: bankOnlyInvoices,
      },
      {
        key: 'cardOnly' as const,
        label: { id: 'PAYABLE_BY_CC_ONLY', defaultMessage: 'Payable by card only' },
        invoices: cardOnlyInvoices,
      },
    ];

    return [...categoriesArray].sort((a, b) => {
      const aHasDefault = a.invoices.some((inv) => inv.defaultInvoice);
      const bHasDefault = b.invoices.some((inv) => inv.defaultInvoice);
      if (aHasDefault && !bHasDefault) return -1;
      if (!aHasDefault && bHasDefault) return 1;
      return 0;
    });
  }, [bankOrCardInvoices, bankOnlyInvoices, cardOnlyInvoices]);

  const selectedCategories = useMemo(() => {
    const selected = {
      bankOrCard: false,
      bankOnly: false,
      cardOnly: false,
    };

    for (const { key, invoices } of categories) {
      const hasSelections = invoices.some((inv) => inv.selected);
      if (hasSelections) {
        selected[key] = true;
      }
    }

    return selected;
  }, [categories]);

  const handleToggleShowAll = useCallback((category: 'bankOrCard' | 'bankOnly' | 'cardOnly') => {
    setShowAllInvoices((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  }, []);

  const INVOICE_DISPLAY_THRESHOLD = 5;

  const getDisplayedInvoices = useCallback(
    (invoices: UnpaidInvoice[], category: 'bankOrCard' | 'bankOnly' | 'cardOnly') => {
      return showAllInvoices[category] ? invoices : invoices.slice(0, INVOICE_DISPLAY_THRESHOLD);
    },
    [showAllInvoices]
  );

  const handleSelectAllCheck = useCallback(
    (categoryInvoices: UnpaidInvoice[]) => {
      // Use saleId as the unique identifier (always present and unique)
      const categorySaleIds = categoryInvoices.map((inv) => inv.saleId);
      const areAllCategorySelected = categoryInvoices
        .filter(
          (inv) => inv.defaultInvoice || inv.balance <= maxSelectableBalance // Default or within limit
        )
        .every((inv) => inv.selected);

      setBatchInvoices((prev) =>
        prev?.map((invoice) => {
          if (categorySaleIds.includes(invoice.saleId)) {
            // If default invoice is over limit, skip all non-default invoices when checking
            if (!areAllCategorySelected && isDefaultInvoiceOverLimit && !invoice.defaultInvoice) {
              return invoice;
            }
            // Skip non-default invoices over the limit when checking
            if (
              !areAllCategorySelected &&
              !invoice.defaultInvoice &&
              invoice.balance > maxSelectableBalance
            ) {
              return invoice;
            }
            // Toggle selection: if all are selected, uncheck all (including default)
            // If not all selected, check all selectable ones
            return {
              ...invoice,
              selected: !areAllCategorySelected,
            };
          }
          return invoice;
        })
      );
    },
    [setBatchInvoices, maxSelectableBalance, isDefaultInvoiceOverLimit]
  );

  // Count how many categories have invoices
  const categoriesWithInvoices = useMemo(() => {
    return categories.filter((cat) => cat.invoices.length > 0).length;
  }, [categories]);

  // Only show category labels when there are multiple categories
  const shouldShowCategoryLabels = categoriesWithInvoices > 1;

  return (
    <>
      {categories.map(({ key, label, invoices }) => {
        let isCategoryDisabled = false;

        if (key === 'bankOnly') {
          isCategoryDisabled = selectedCategories.cardOnly;
        } else if (key === 'cardOnly') {
          isCategoryDisabled = selectedCategories.bankOnly;
        }

        return (
          invoices.length > 0 && (
            <div key={key} className="invoice-table-container">
              {shouldShowCategoryLabels && (
                <span className="invoice-row-wrapper data-row bank-cc-row">
                  <FormattedMessage id={label.id} defaultMessage={label.defaultMessage} />
                </span>
              )}
              <BatchInvoiceTableHeaderRow
                isAllChecked={saleSelectors.isCategoryAllChecked(invoices)}
                handleSelectAllCheck={() => handleSelectAllCheck(invoices)}
                disabled={isCategoryDisabled}
              />
              {getDisplayedInvoices(invoices, key).map((invoice: UnpaidInvoice, index: number) => (
                <BatchInvoiceDataRow
                  key={`${key}-${invoice.saleId || index}`}
                  invoice={invoice}
                  index={index}
                  handleCheckboxChange={handleCheckboxChange}
                  handleShowToolTip={handleShowToolTip}
                  tooltipStates={tooltipStates}
                  handleDocumentClick={handleDocumentClick}
                  handleNewWindowClick={handleNewWindowClick}
                  currency={currency}
                  disabled={
                    isCategoryDisabled || (isDefaultInvoiceOverLimit && !invoice.defaultInvoice)
                  }
                  maxSelectableBalance={maxSelectableBalance}
                  partialPaymentAmount={partialPaymentAmount}
                />
              ))}
              {saleSelectors.shouldShowCategoryToggleButton(invoices) && (
                <div className="invoice-row-wrapper data-row">
                  <button className="show-all-text" onClick={() => handleToggleShowAll(key)}>
                    {showAllInvoices[key] ? (
                      <FormattedMessage id="BATCH_SHOW_LESS" defaultMessage="Show less" />
                    ) : (
                      <FormattedMessage id="BATCH_SHOW_ALL" defaultMessage="Show all" />
                    )}
                  </button>
                </div>
              )}
            </div>
          )
        );
      })}

      <style jsx>{`
        .invoice-table-container {
          background-color: ${colors.white};
          border-radius: 8px;
          border: 1px solid ${colors.gray06};
          overflow: hidden;
          margin-bottom: 16px;
        }

        .invoice-row-wrapper {
          display: flex;
          width: 100%;
          height: 40px;
          padding: 12px;
          justify-content: space-between;
          align-items: center;
        }

        .data-row {
          background-color: ${colors.white};
          border-bottom: 1px solid ${colors.gray06};
        }

        .data-row:last-child {
          border-bottom: none;
        }

        .bank-cc-row {
          font-family: AvenirNextforINTUIT-Medium;
          font-size: ${fontSize.xs};
          color: ${colors.black};
        }

        .show-all-text {
          font-family: AvenirNextforINTUIT-Demi;
          font-weight: bold;
          font-size: ${fontSize.xxs};
          color: ${colors.gray};
          border: none;
          border-radius: 4px;
          width: 85px;
          height: 24px;
          background-color: ${colors.gray06};
          cursor: pointer;
        }

        .show-all-text:hover {
          background-color: ${colors.gray05};
        }
      `}</style>
    </>
  );
};

export default React.memo(BatchInvoiceMixedPaymentListTable);
